import { useEffect, lazy, Suspense } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/routes';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { useBasketStore } from '@/stores/basket';
import { Toast } from '@/components/common/Toast';
import { usePageTracking } from '@/hooks/usePageTracking';
import { usePageViewLogger } from '@/hooks/usePageViewLogger';

// Only load DevTools in development and lazily
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((mod) => ({
        default: mod.ReactQueryDevtools,
      }))
    )
  : () => null;

function ToastContainer() {
  const { toasts, removeToast } = useUIStore();

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasCheckedAuth = useAuthStore((state) => state.hasCheckedAuth);

  useEffect(() => {
    // Only check auth after hydration completes
    if (isHydrated) {
      checkAuth();
    }
  }, [isHydrated, checkAuth]);

  // Hydrate basket from API once auth is confirmed — syncs across devices.
  // Basket is cleared on explicit logout (in auth.ts), not here, to preserve
  // potential guest basket state for future guest-to-member merge (R6).
  useEffect(() => {
    if (hasCheckedAuth && isAuthenticated) {
      useBasketStore.getState().hydrateFromApi();
    }
  }, [hasCheckedAuth, isAuthenticated]);

  // Show nothing until hydration completes (prevents flash)
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  usePageTracking();
  usePageViewLogger();

  // Aggressively unregister all service workers on every load
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        console.log('🔍 Found service workers:', registrations.length);
        registrations.forEach((registration) => {
          registration.unregister().then(() => {
            console.log('✅ Unregistered service worker:', registration.scope);
          });
        });
        if (registrations.length > 0) {
          console.log('🧹 All service workers unregistered - page will use fresh content');
        }
      });
    }

    // Clear any stale caches
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          if (cacheName.includes('pce-') || cacheName.includes('workbox')) {
            caches.delete(cacheName).then(() => {
              console.log('🗑️ Deleted cache:', cacheName);
            });
          }
        });
      });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthInitializer>
        <RouterProvider router={router} />
        <ToastContainer />
      </AuthInitializer>
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

export default App;
